import { useId, useCallback, useEffect, useState } from 'react';
import { VodapayEvent } from 'components/_global/vodapay';
import { Box, Flex } from 'components/box';
import { Text } from 'components/typography';
import { closeDrawer } from '../../../../redux/global/actions';
import { useDispatch } from 'react-redux';
import { useVodapay, useSetVodapayEventListeners } from 'hooks/app';
import { useSocialLogin } from 'hooks/customer';
import { css } from '@emotion/react';
import Button from 'components/button';
import Loading from 'components/loading';
import VodaPayLogo from 'shared/assets/icons/vodapay.svg';
import { LoginMethodEnum } from 'types/third-party/gtm';

const SOCIAL_LOGIN_PROVIDER = 'VODAPAY';

interface AuthSuccessResponse {
  authCode: string;
}

const isValidAuthSuccessResponse = (
  res: AuthSuccessResponse | unknown
): res is AuthSuccessResponse =>
  !!res &&
  typeof res === 'object' &&
  'authCode' in res &&
  typeof (res as AuthSuccessResponse).authCode === 'string';

const VodapayAuth = () => {
  const idRef = useId();

  const dispatch = useDispatch();

  const vodapay = useVodapay();
  const setListeners = useSetVodapayEventListeners();

  const [socialLogin, { loading, error, completed }] = useSocialLogin(
    LoginMethodEnum.VODAPAY
  );

  const [authError, setAuthError] = useState('');

  useEffect(() => {
    if (completed) {
      dispatch(closeDrawer());
    }
  }, [dispatch, completed]);

  const callback = useCallback(
    (data: unknown) => {
      if (isValidAuthSuccessResponse(data)) {
        socialLogin({
          provider: SOCIAL_LOGIN_PROVIDER,

          // (on device)
          accessToken: data.authCode,

          // (simulator) need to generate an authCode each time via applyAuthCode
          // accessToken: '0000000001bxk2OlphgY350f00169507',
        });
      }
    },
    [socialLogin]
  );

  useEffect(() => {
    setListeners(listeners => [
      ...listeners,
      { id: `${idRef}.success`, event: VodapayEvent.AuthSuccess, callback },
      {
        id: `${idRef}.failure`,
        event: VodapayEvent.AuthFailure,
        callback: (data: unknown) =>
          setAuthError(`Authorization rejected: ${JSON.stringify(data)}`),
      },
    ]);

    return () => {
      setListeners(listeners =>
        listeners.filter(
          ({ id }) => id !== `${idRef}.success` && id !== `${idRef}.failure`
        )
      );
    };
  }, [callback, idRef, setListeners]);

  return (
    <Loading isLoading={loading}>
      <Flex
        justifyContent="center"
        alignItems="center"
        alignContent="center"
        flexWrap="wrap"
        flexDirection="column"
        mt={3}
        css={css`
          width: 100%;
          height: 60vh;
        `}
      >
        <Button
          onClick={() => vodapay.authInit()}
          type="button"
          variant="flat"
          css={css`
            width: 100%;
          `}
        >
          <Flex
            width="100%"
            alignItems="center"
            borderRadius={1}
            css={theme => css`
              border: 1px solid ${theme.colors.vodapayRed};
            `}
          >
            <Box
              py={2}
              px="0.9rem"
              backgroundColor="vodapayRed"
              css={theme => css`
                border-right: 1px solid ${theme.colors.background};
              `}
            >
              <VodaPayLogo />
            </Box>
            <Text
              color="vodapayRed"
              fontSize="r"
              fontWeight={700}
              fontFamily="header"
              m="0 auto"
              letterSpacing="0.8px"
              css={theme => css`
                text-transform: uppercase;
                @media ${theme.mediaQueries.tinyScreenOnly} {
                  font-size: ${theme.fontSizes.s};
                }
              `}
            >
              Log in with VodaPay
            </Text>
          </Flex>
        </Button>

        {(!!authError || !!error) && (
          <Text my={3} color="danger" fontWeight="bold" textAlign="center">
            Login failed. Please try again.
          </Text>
        )}
      </Flex>
    </Loading>
  );
};

export default VodapayAuth;
